<div class="inbound-form-wrapper-container">
  <!-- Sticky Header -->
  <div class="sticky-header">
    <div class="flex justify-between items-center mb-5">
      <h2 class="text-2xl font-semibold text-gray-800">
        {{ pageTitle() | translate }}
      </h2>
      <div class="flex gap-3">
        <app-button
          testId="internal-return-view-back-btn"
          [label]="'common.actions.back' | translate"
          (emitOnClick)="onBackClick()"
          [outlined]="true"
          type="secondary"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="inbound-form-wrapper-content">
    <div class="p-2">
      @if (loading()) {
        <div class="flex justify-center items-center py-8">
          <p-progressSpinner data-testid="internal-return-view-loading"></p-progressSpinner>
        </div>
      } @else {
        @if (formData()) {
          <!-- Basic Information Section -->
          <section class="form-section pl-6 mb-4 notice-basic-section">
            <div class="flex items-center px-4">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{
                  "after.internal.return.view.sections.basic_information"
                    | translate
                }}
              </h2>
            </div>
            <div class="grid gap-2 pl-8 pr-4 py-2 grid-cols-4">
              <!-- Row 1: Internal Return No, Internal Order No, Status, Return Type -->
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.internal_return_no" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("internalReturnNo") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.internal_order_no" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("internalOrderNo") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.status" | translate }}
                </span>
                <div class="mt-1">
                  <p-tag
                    data-testid="internal-return-view-status-tag"
                    [value]="getStatusLabel(getFieldValue('status'))"
                    [ngClass]="getStatusTagClass(getFieldValue('status'))"
                    styleClass="status-tag"
                  />
                </div>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.return_type" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnType") || "-" }}
                </p>
              </div>
              <!-- Row 2: Return Refund Reason, Return Qty, Buyer Refund Total, Settlement Method -->
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.return_refund_reason"
                      | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnRefundReason") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.return_qty" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnQty") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.buyer_refund_total" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ formatCurrency(formData()?.buyerRefundTotal) }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.settlement_method" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{
                    formatSettlementMethod(getFieldValue("settlementMethod"))
                  }}
                </p>
              </div>
              <!-- Row 3: Internal Create Time, CS Notes -->
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.internal_created_at"
                      | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ formatDate(getFieldValue("creationDate")) || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.service_notes" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("csNotes") || "-" }}
                </p>
              </div>
            </div>
          </section>

          <!-- Store Information Section -->
          <section class="form-section pl-6 mb-4 notice-basic-section">
            <div class="flex items-center px-4">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{
                  "after.internal.return.view.sections.store_information"
                    | translate
                }}
              </h2>
            </div>
            <div class="grid gap-2 pl-8 pr-4 py-2 grid-cols-4">
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.sales_store" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("salesStore") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.sales_channel" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("salesChannel") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.receiving_warehouse"
                      | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("receivingWarehouse") || "-" }}
                </p>
              </div>
            </div>
          </section>

          <!-- Customer Information Section -->
          <section class="form-section pl-6 mb-4 notice-basic-section">
            <div class="flex items-center px-4">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{
                  "after.internal.return.view.sections.customer_information"
                    | translate
                }}
              </h2>
            </div>
            <div class="grid gap-2 pl-8 pr-4 py-2 grid-cols-4">
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.buyer_nickname" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("buyerNickname") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.returner" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnerName") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.returner_phone" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnerPhone") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{
                    "after.internal.return.table.returner_address" | translate
                  }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("returnerAddress") || "-" }}
                </p>
              </div>
              <div class="form-field">
                <span class="form-label text-sm font-semibold text-gray-700">
                  {{ "after.internal.return.table.buyer_notes" | translate }}
                </span>
                <p class="text-sm text-gray-800 mt-1">
                  {{ getFieldValue("buyerNotes") || "-" }}
                </p>
              </div>
            </div>
          </section>

          <!-- Product Information Section -->
          <section class="form-section mb-4">
            <div class="flex items-center mb-2 px-4">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{
                  "after.internal.return.view.sections.product_information"
                    | translate
                }}
              </h2>
            </div>
            <div class="pl-6 ml-4 py-1">
              @if (productDetails().length > 0) {
                <app-data-table
                  data-testid="internal-return-view-product-table"
                  [columns]="productTableColumns()"
                  [dataSource]="productDetails()"
                  [loading]="loading()"
                  [hasCheckColumn]="false"
                  [totalRecords]="productDetails().length"
                  [first]="0"
                  [rows]="20"
                ></app-data-table>
              } @else {
                <div class="text-center text-gray-500 py-4">
                  {{
                    "after.internal.return.view.messages.no_products"
                      | translate
                  }}
                </div>
              }
            </div>
          </section>
        }
      }
    </div>
  </div>

  <!-- Toast Messages -->
  <p-toast data-testid="internal-return-view-toast"></p-toast>
</div>
